// The one day-model both پذیرش surfaces (web + mobile) render from.
// DESIGN-CONCEPT §4.3/§4.4: platform files are render-only; who is next, what the
// queue order is and what the day's counts are must be a single computation, or the
// two surfaces will eventually disagree about which patient walks in next.
import { queueFor } from '../../hooks/useReception';
// The status vocabulary lives in lib/appointments — one definition for both
// render surfaces. Imported for use below AND re-exported, because every call
// site in this screen already reaches for the day model.
import { isDone, isHere, isReadOnly } from '../../lib/appointments';

export { isDone, isHere, isReadOnly };

export const toMinutes = (t = '00:00') => {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// ISO-date (YYYY-MM-DD) and Jalali-week arithmetic is the kit's, shared with the
// clinic's پذیرش (PM-0052): `shiftISO`, `irWeekday` (0 = شنبه … 6 = جمعه),
// `jalaliWeekStart`, `weekDaysFrom`. Re-exported so every call site keeps
// reaching for the day model.
import { shiftISO, irWeekday, jalaliWeekStart, weekDaysFrom } from '@parsina/framework/ui/lib/jalaliWeek';
import { msToJalali, IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';
import { calendarSystem } from '@parsina/framework/ui/lib/brand';
export { shiftISO, irWeekday, jalaliWeekStart, weekDaysFrom };

/**
 * The day of the month the desktop week strip shows for an ISO date: Jalali, in
 * every language — unless the brand reads the Gregorian calendar
 * (`calendar: 'gregory'`, docs/rebranding/CONTRACT.md), whose day the ISO date
 * already is. Undefined for a date that is not one.
 */
export function stripDayOfMonth(iso) {
  if (calendarSystem('jalali') === 'gregory') {
    const day = Number(String(iso).slice(8, 10));
    return Number.isInteger(day) && day > 0 ? day : undefined;
  }
  return msToJalali(Date.parse(`${iso}T12:00:00Z`) + IRAN_OFFSET_MS)?.d;
}

// «شنبه، ۲۶ تیر» / «Saturday, 17 Jul» (+ year when asked).
//
// The Jalali arithmetic that used to live here is gone: the i18n formatters
// already render this instant in the active locale's calendar, digits and
// weekday names, pinned to Iran time. Keeping a second hand-rolled Jalali
// implementation here would have meant the day navigator and the rest of the
// app could disagree about what day it is — and it could only ever speak
// Persian.
//
// `formatDate` comes from `useTranslation()`. It is passed in rather than
// imported so this module stays a pure day-model with no React dependency,
// which is what lets the tests call it directly.
export function makeDateLabel(ms, { formatDate, withYear = false } = {}) {
  if (!formatDate || ms == null) return '';
  return formatDate(ms, withYear ? 'full' : 'weekdayDayMonth');
}

// «۲۶ تیر» / «17 Jul» — the short form for section titles.
export function makeShortLabel(ms, { formatDate } = {}) {
  if (!formatDate || ms == null) return '';
  return formatDate(ms, 'dayMonth');
}

// The selected day, derived once: clock-sorted agenda, the doctor-room queue
// (today only — on any other day the desk's ordering is meaningless), the split
// into upcoming/done, waiting-line positions, and who the doctor calls in next
// (head of the queue when anyone is here; otherwise the next patient by the clock).
export function buildDayModel({ appointments, isToday, nowMin }) {
  const dayApts = [...appointments].sort((a, b) => toMinutes(a.time) - toMinutes(b.time));
  const queue = isToday ? queueFor(dayApts) : [];
  const queueIds = new Set(queue.map(a => a.id));
  const upcoming = dayApts.filter(a => !queueIds.has(a.id) && !isDone(a.status));
  const done = dayApts.filter(a => isDone(a.status));
  const queuePosOf = new Map(
    queue.filter(a => a.status === 'arrived').map((a, i) => [a.id, i + 1]),
  );
  const nextApt = isToday
    ? queue[0] || upcoming.find(a => toMinutes(a.time) >= nowMin) || null
    : null;
  return { dayApts, queue, queueIds, upcoming, done, queuePosOf, nextApt };
}

// The mobile «پیش رو» list: the queue first (never filtered by the clock — a patient
// sitting past their slot time is still sitting there), then the ones still to come
// by time. Off today the whole agenda is "ahead".
export function futureListFor(model, { isToday, nowMin }) {
  const stillToCome = model.upcoming.filter(a => !isToday || toMinutes(a.time) >= nowMin);
  return [...model.queue, ...stillToCome.filter(a => !model.queueIds.has(a.id))];
}

// One count source for both KPI strips — derived from the same list the rows
// render, so the numbers can never disagree with the rows below them.
export function dayCounts(dayApts) {
  return {
    total: dayApts.length,
    here: dayApts.filter(a => isHere(a.status)).length,
    scheduled: dayApts.filter(a => a.status === 'scheduled').length,
    waiting: dayApts.filter(a => a.status === 'scheduled' || a.status === 'arrived').length,
    completed: dayApts.filter(a => a.status === 'completed').length,
    cancelled: dayApts.filter(a => a.status === 'cancelled').length,
  };
}

// ── «کجا هستم» — where the schedule puts the doctor ───────────────────────────
//
// The source is scheduling's `doctor_presences`, the rows booking is enforced
// against, delivered by the doctor BFF's `myPresenceSchedule` already in the
// Iranian week (0 = شنبه) and Iran-local dates. The matching rule is the
// SERVICE'S: a window applies to a day when it is a weekly row for that weekday
// OR a one-off row for that date — a UNION, not an override (the service's
// `_presence_windows_on` comment says a one-off wins; its code unions, and the
// desk books by the code). Windows are half-open [start, end).

/** The windows that apply to `iso`, restricted to `memberIds` when given. */
export function windowsOn(windows, iso, memberIds) {
  const dow = irWeekday(iso);
  return (windows || []).filter((w) => {
    if (memberIds && !memberIds.has(w.clinicId)) return false;
    return w.specificDate ? w.specificDate === iso : w.dayOfWeek === dow;
  });
}

// Touching or overlapping windows at ONE clinic are one shift: 08:00–12:00 +
// 12:00–14:00 reads «تا ۱۴:۰۰», not «تا ۱۲:۰۰» (the service merges the same way).
export function mergeWindows(windows) {
  const byClinic = new Map();
  for (const w of windows || []) {
    if (!(w.endMinute > w.startMinute)) continue;
    if (!byClinic.has(w.clinicId)) byClinic.set(w.clinicId, []);
    byClinic.get(w.clinicId).push({ clinicId: w.clinicId, start: w.startMinute, end: w.endMinute });
  }
  const out = [];
  for (const list of byClinic.values()) {
    list.sort((a, b) => a.start - b.start);
    let cur = null;
    for (const w of list) {
      if (cur && w.start <= cur.end) cur.end = Math.max(cur.end, w.end);
      else { cur = { ...w }; out.push(cur); }
    }
  }
  return out.sort((a, b) => a.start - b.start || a.end - b.end);
}

/**
 * What the place line says for the selected day, and which clinic the AUTO
 * lens should follow.
 *
 * state: unknown | no_clinic | no_shift | other_day | overlap | in_shift |
 *        between | before_first | done
 *
 * `status` is the presence fetch's: anything but 'ready' is `unknown` — a
 * failed or pending lookup must never read as «no shift registered».
 * `scheduledClinicId` is null whenever the schedule does not name exactly one
 * clinic (= the lens shows every clinic).
 */
export function resolvePlace({ windows, status = 'ready', clinics = [], dayISO, isToday, nowMin }) {
  if (!clinics.length) return { state: 'no_clinic', scheduledClinicId: null };
  if (status !== 'ready') return { state: 'unknown', scheduledClinicId: null };

  const memberIds = new Set(clinics.map((c) => c.id));
  const day = mergeWindows(windowsOn(windows, dayISO, memberIds));
  const distinct = (list) => [...new Set(list.map((w) => w.clinicId))];

  if (!day.length) return { state: 'no_shift', scheduledClinicId: null, day };

  if (!isToday) {
    const ids = distinct(day);
    return { state: 'other_day', day, clinicIds: ids, scheduledClinicId: ids.length === 1 ? ids[0] : null };
  }

  const active = day.filter((w) => w.start <= nowMin && nowMin < w.end);
  if (distinct(active).length > 1) {
    return { state: 'overlap', day, clinicIds: distinct(active), scheduledClinicId: null };
  }
  if (active.length) return { state: 'in_shift', day, window: active[0], scheduledClinicId: active[0].clinicId };

  const next = day.find((w) => w.start > nowMin);
  if (next) {
    const endedBefore = day.some((w) => w.end <= nowMin);
    return { state: endedBefore ? 'between' : 'before_first', day, window: next, scheduledClinicId: next.clinicId };
  }
  const last = day.reduce((a, b) => (b.end >= a.end ? b : a));
  return { state: 'done', day, window: last, scheduledClinicId: last.clinicId };
}

/** Minutes after midnight → "HH:MM" (Latin digits; the caller localises). */
export const minutesToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// An hour range as ONE left-to-right island: without the isolate, an RTL
// paragraph reorders «08:00–12:00» into «12:00–08:00». `fmt` localises digits.
export const hoursRange = (start, end, fmt = (x) => x) =>
  `⁦${fmt(minutesToHHMM(start))}–${fmt(minutesToHHMM(end))}⁩`;
